import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MOCK_AGREEMENTS, EMPTY_AGREEMENT } from '../../data/mockAgreements';

const UPLOAD_STATUS_OPTIONS = [
  { value: 'NEEDS_CONTRACTS_REVIEW', label: 'Redlined \u2014 Contracts Review Needed' },
  { value: 'PENDING_REVIEW', label: 'Not Redlined \u2014 No Contracts Review Needed' },
];

const TERM_DURATION_OPTIONS = ['1 year', '2 years', '3 years', '5 years'];

const ACCEPTED_FILE_TYPES = {
  'application/pdf': '.pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
};

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const STATUS_TABS = [
  { key: 'ALL', label: 'All', statuses: ['NEEDS_CONTRACTS_REVIEW', 'FULLY_EXECUTED', 'REJECTED', 'EXPIRED'] },
  { key: 'NEEDS_CONTRACTS_REVIEW', label: 'Contracts Review', statuses: ['NEEDS_CONTRACTS_REVIEW'] },
  { key: 'FULLY_EXECUTED', label: 'Executed', statuses: ['FULLY_EXECUTED'] },
  { key: 'REJECTED', label: 'Rejected', statuses: ['REJECTED'] },
  { key: 'EXPIRED', label: 'Expired', statuses: ['EXPIRED'] },
];

const STATUS_DISPLAY = {
  NEEDS_CONTRACTS_REVIEW: { label: 'Contracts Review', className: 'review' },
  FULLY_EXECUTED: { label: 'Executed', className: 'active' },
  REJECTED: { label: 'Rejected', className: 'critical' },
  EXPIRED: { label: 'Expired', className: 'critical' },
};

const TYPE_DISPLAY = {
  NDA: { label: 'NDA', className: 'accepted' },
  TA: { label: 'TA', className: 'completed' },
};

function formatDate(iso) {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function AgreementReview({ onNavigate, user }) {
  const [activeTab, setActiveTab] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // PR 2.5: Additional filters
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadForm, setUploadForm] = useState({ ...EMPTY_AGREEMENT });
  const [uploadFormErrors, setUploadFormErrors] = useState({});
  const [uploadFile, setUploadFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadOrigin, setUploadOrigin] = useState('');
  const [uploadSignedByCounterparty, setUploadSignedByCounterparty] = useState('');

  const showUploadFormFields = uploadOrigin === 'counterparty' || (uploadOrigin === 'bcore' && uploadSignedByCounterparty === 'yes');

  const handleFormChange = (field, value) => {
    setUploadForm((prev) => ({ ...prev, [field]: value }));
    if (uploadFormErrors[field]) {
      setUploadFormErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
    }
  };

  const validateUploadForm = () => {
    const errs = {};
    if (!uploadForm.type) errs.type = 'Required';
    if (!uploadForm.status) errs.status = 'Required';
    if (!uploadForm.counterpartyName.trim()) errs.counterpartyName = 'Required';
    if (!uploadForm.termsType) errs.termsType = 'Required';
    if (!uploadForm.termDuration) errs.termDuration = 'Required';
    setUploadFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleUploadSubmit = (e) => {
    e.preventDefault();
    if (!validateUploadForm()) return;
    const now = new Date().toISOString();
    // Workflow: Redlined → Contracts Review; Not Redlined → Executed immediately
    const effectiveStatus = uploadForm.status === 'NEEDS_CONTRACTS_REVIEW'
      ? 'NEEDS_CONTRACTS_REVIEW'
      : 'FULLY_EXECUTED';
    const newAgreement = {
      id: `agr-${Date.now()}`,
      type: uploadForm.type,
      status: effectiveStatus,
      counterpartyName: uploadForm.counterpartyName.trim(),
      opportunityOwner: uploadForm.opportunityOwner.trim() || 'Unknown',
      opportunityName: uploadForm.opportunityName.trim() || '',
      termDuration: uploadForm.termDuration,
      termsType: uploadForm.termsType,
      signedDate: null,
      expirationDate: null,
      createdAt: now,
      updatedAt: now,
      createdBy: uploadForm.opportunityOwner.trim() || 'user@bcore.com',
      notes: uploadForm.notes.trim(),
    };
    setItems((prev) => [newAgreement, ...prev]);
    closeUploadModal();
  };

  const handleContractsAction = (id, action) => {
    const now = new Date().toISOString();
    setItems((prev) => prev.map((item) => {
      if (item.id !== id) return item;
      if (action === 'approve') {
        return { ...item, status: 'FULLY_EXECUTED', signedDate: now, updatedAt: now };
      }
      return { ...item, status: 'REJECTED', updatedAt: now };
    }));
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadForm({ ...EMPTY_AGREEMENT });
    setUploadFormErrors({});
    setUploadFile(null);
    setDragActive(false);
    setUploadOrigin('');
    setUploadSignedByCounterparty('');
  };

  const handleFile = (file) => {
    if (!file) return;
    if (!ACCEPTED_FILE_TYPES[file.type]) {
      setUploadFormErrors((prev) => ({ ...prev, file: 'Only PDF and DOCX files are accepted' }));
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setUploadFormErrors((prev) => ({ ...prev, file: 'File must be under 50 MB' }));
      return;
    }
    setUploadFile(file);
    setUploadFormErrors((prev) => { const n = { ...prev }; delete n.file; return n; });
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  };

  // Simulate loading from API (will be replaced with real fetch later)
  useEffect(() => {
    const timer = setTimeout(() => {
      setItems(MOCK_AGREEMENTS);
      setLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const handleSort = useCallback((key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }, [sortKey]);

  const sortIndicator = (key) => {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' \u25B2' : ' \u25BC';
  };

  const tabDef = STATUS_TABS.find((t) => t.key === activeTab) || STATUS_TABS[0];

  const tabCounts = useMemo(() => {
    const counts = {};
    STATUS_TABS.forEach(({ key, statuses }) => {
      counts[key] = items.filter((a) => statuses.includes(a.status)).length;
    });
    return counts;
  }, [items]);

  const filtered = useMemo(() => {
    let result = items.filter((a) => tabDef.statuses.includes(a.status));

    // Type filter
    if (typeFilter !== 'ALL') {
      result = result.filter((a) => a.type === typeFilter);
    }

    // Text search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((a) =>
        (a.counterpartyName || '').toLowerCase().includes(q) ||
        (a.opportunityName || '').toLowerCase().includes(q) ||
        (a.opportunityOwner || '').toLowerCase().includes(q)
      );
    }

    // Date range filter (on createdAt)
    if (dateFrom) {
      const from = new Date(dateFrom + 'T00:00:00');
      result = result.filter((a) => new Date(a.createdAt) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo + 'T23:59:59');
      result = result.filter((a) => new Date(a.createdAt) <= to);
    }

    result.sort((a, b) => {
      let av, bv;
      if (sortKey === 'createdAt' || sortKey === 'expirationDate') {
        av = new Date(a[sortKey] || 0);
        bv = new Date(b[sortKey] || 0);
      } else {
        av = (a[sortKey] || '').toLowerCase();
        bv = (b[sortKey] || '').toLowerCase();
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [items, tabDef.statuses, typeFilter, searchQuery, dateFrom, dateTo, sortKey, sortDir]);

  /** CSV export using the DeliveryDashboard pattern. */
  const handleExport = useCallback(() => {
    const columns = [
      { label: 'Type', key: 'type' },
      { label: 'Counterparty', key: 'counterpartyName' },
      { label: 'Short Name', key: 'counterpartyShortName' },
      { label: 'Opportunity', key: 'opportunityName' },
      { label: 'Owner', key: 'opportunityOwner' },
      { label: 'Status', key: 'status' },
      { label: 'Terms', key: 'termsType' },
      { label: 'Duration', key: 'termDuration' },
      { label: 'Signed', key: 'signedDate' },
      { label: 'Expires', key: 'expirationDate' },
      { label: 'Created', key: 'createdAt' },
    ];
    const header = columns.map((c) => c.label).join(',');
    const rows = filtered.map((a) =>
      columns.map((col) => {
        const val = a[col.key];
        if (val == null || val === '') return '';
        const str = String(val);
        if (str.includes(',')) return `"${str}"`;
        return str;
      }).join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `agreements-export-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [filtered]);

  const summary = useMemo(() => ({
    total: items.length,
    draft: items.filter((a) => a.status === 'DRAFT').length,
    pendingReview: items.filter((a) => a.status === 'PENDING_REVIEW').length,
    contractsReview: items.filter((a) => a.status === 'NEEDS_CONTRACTS_REVIEW').length,
    executed: items.filter((a) => a.status === 'FULLY_EXECUTED').length,
  }), [items]);

  const isExpiredTab = activeTab === 'EXPIRED';

  return (
    <div className="contracts-page" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
      {/* Summary Bar */}
      <div className="contracts-summary">
        <div className="contracts-summary-item">
          <span className="contracts-summary-label">Total</span>
          <span className="contracts-summary-value">{summary.total}</span>
        </div>
        <div className="contracts-summary-item">
          <span className="contracts-summary-label">Draft</span>
          <span className="contracts-summary-value" style={{ color: 'var(--status-pending)' }}>{summary.draft}</span>
        </div>
        <div className="contracts-summary-item">
          <span className="contracts-summary-label">Pending Review</span>
          <span className="contracts-summary-value" style={{ color: 'var(--status-review)' }}>{summary.pendingReview}</span>
        </div>
        <div className="contracts-summary-item">
          <span className="contracts-summary-label">Contracts Review</span>
          <span className="contracts-summary-value" style={{ color: 'var(--status-review)' }}>{summary.contractsReview}</span>
        </div>
        <div className="contracts-summary-item">
          <span className="contracts-summary-label">Executed</span>
          <span className="contracts-summary-value" style={{ color: 'var(--status-active)' }}>{summary.executed}</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="page-toolbar">
        <div className="page-toolbar-left" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <div className="search-wrapper">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="Search agreements..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="form-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={{ width: '100px', fontSize: '12px', padding: '6px 8px' }}
          >
            <option value="ALL">All Types</option>
            <option value="NDA">NDA</option>
            <option value="TA">TA</option>
          </select>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '11px', color: 'var(--bcore-text-dim)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>From</span>
            <input
              type="date"
              className="form-input"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{ width: '130px', fontSize: '12px', padding: '5px 8px' }}
            />
            <span style={{ fontSize: '11px', color: 'var(--bcore-text-dim)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>To</span>
            <input
              type="date"
              className="form-input"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{ width: '130px', fontSize: '12px', padding: '5px 8px' }}
            />
          </div>
        </div>
        <div className="page-toolbar-right" style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-outline" onClick={handleExport} title="Export CSV">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export CSV
          </button>
          <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            Upload Agreement
          </button>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="tab-bar">
        {STATUS_TABS.map(({ key, label }) => (
          <button
            key={key}
            className={`tab-btn${activeTab === key ? ' active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            {label}
            <span className="count">{tabCounts[key] || 0}</span>
          </button>
        ))}
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: 'var(--bcore-text-dim)',
          fontSize: '13px',
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}>
          Loading agreements...
        </div>
      )}

      {/* Empty State */}
      {!loading && filtered.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: 'var(--bcore-text-dim)',
          fontSize: '13px',
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: '16px', opacity: 0.4 }}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <div style={{ textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
            {items.length === 0 ? 'No agreements yet' : 'No matching agreements'}
          </div>
          <div style={{ fontSize: '12px' }}>
            {items.length === 0
              ? 'Agreements will appear here as NDAs and TAs are created or uploaded'
              : 'Try adjusting your search or filters'}
          </div>
        </div>
      )}

      {/* Data Table */}
      {!loading && filtered.length > 0 && (
        <div className="data-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Type</th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('counterpartyName')}
                >
                  Counterparty{sortIndicator('counterpartyName')}
                </th>
                <th>Opportunity</th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('opportunityOwner')}
                >
                  Owner{sortIndicator('opportunityOwner')}
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('status')}
                >
                  Status{sortIndicator('status')}
                </th>
                <th>Terms</th>
                <th>Duration</th>
                <th>Signed</th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('expirationDate')}
                >
                  Expires{sortIndicator('expirationDate')}
                </th>
                <th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('createdAt')}
                >
                  Created{sortIndicator('createdAt')}
                </th>
                <th>Reviewer</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => {
                const statusInfo = STATUS_DISPLAY[item.status] || { label: item.status, className: '' };
                const typeInfo = TYPE_DISPLAY[item.type] || { label: item.type, className: '' };

                return (
                  <tr
                    key={item.id}
                    style={isExpiredTab ? { opacity: 0.7 } : undefined}
                  >
                    <td>
                      <span className={`badge-status ${typeInfo.className}`} style={{ fontSize: '10px' }}>
                        {typeInfo.label}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontWeight: 500, color: 'var(--bcore-text-primary)' }}>
                        {item.counterpartyName}
                      </span>
                    </td>
                    <td>{item.opportunityName || '--'}</td>
                    <td>{item.opportunityOwner}</td>
                    <td>
                      <span className={`badge-status ${statusInfo.className}`}>
                        <span className="badge-dot"></span>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td>
                      <span className={`tag${item.termsType === 'non-standard' ? ' accent' : ''}`}>
                        {item.termsType}
                      </span>
                    </td>
                    <td>{item.termDuration}</td>
                    <td>{formatDate(item.signedDate)}</td>
                    <td>
                      {(() => {
                        if (!item.expirationDate) return '--';
                        const exp = new Date(item.expirationDate);
                        const now = new Date();
                        const daysLeft = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
                        const dateStr = formatDate(item.expirationDate);
                        if (daysLeft < 0) return <span style={{ color: 'var(--status-critical)' }}>{dateStr}</span>;
                        if (daysLeft <= 90) return (
                          <span>
                            <span style={{ color: 'var(--status-pending)' }}>{dateStr}</span>
                            <span className="badge-status pending" style={{ fontSize: '9px', marginLeft: '6px', padding: '1px 6px' }}>
                              Expiring Soon
                            </span>
                          </span>
                        );
                        return dateStr;
                      })()}
                    </td>
                    <td title={item.createdAt}>{formatDate(item.createdAt)}</td>
                    <td>
                      {item.status === 'NEEDS_CONTRACTS_REVIEW' && (
                        <span style={{ fontSize: '11px', color: 'var(--status-review)' }} title="Contracts team review">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px', verticalAlign: '-1px' }}>
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                          Contracts
                        </span>
                      )}
                      {item.status !== 'NEEDS_CONTRACTS_REVIEW' && (
                        <span style={{ fontSize: '11px', color: 'var(--bcore-text-dim)' }}>--</span>
                      )}
                    </td>
                    <td>
                      <div className="table-actions" style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        {item.status === 'NEEDS_CONTRACTS_REVIEW' && (
                          <>
                            <button
                              className="table-action-btn"
                              title="Approve — Mark as Executed"
                              onClick={() => handleContractsAction(item.id, 'approve')}
                              style={{ color: 'var(--status-active, #2ecc71)' }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            </button>
                            <button
                              className="table-action-btn"
                              title="Reject"
                              onClick={() => handleContractsAction(item.id, 'reject')}
                              style={{ color: 'var(--status-critical, #e74c3c)' }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          </>
                        )}
                        <button
                          className="table-action-btn"
                          title="View"
                          onClick={() => onNavigate('agreement-detail', { agreementId: item.id })}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* Upload Modal */}
      {showUploadModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
        }} onClick={closeUploadModal}>
          <div
            style={{
              background: 'var(--bcore-bg-secondary, #242424)',
              borderRadius: '12px',
              padding: '32px',
              width: '100%',
              maxWidth: '700px',
              maxHeight: '85vh',
              overflowY: 'auto',
              border: '1px solid var(--bcore-border, #333)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{
              fontFamily: 'Kode Mono, monospace',
              fontSize: '18px',
              textTransform: 'uppercase',
              marginBottom: '24px',
              color: 'var(--bcore-text-primary)',
            }}>
              Upload Agreement
            </h2>

            <form onSubmit={handleUploadSubmit}>
              {/* Gating Question 1: Who originated the agreement? */}
              <div style={{ marginBottom: '16px' }}>
                <label className="form-label">Who originated this agreement? <span className="required">*</span></label>
                <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                  {[{ value: 'bcore', label: 'B/CORE' }, { value: 'counterparty', label: 'Counterparty' }].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { setUploadOrigin(opt.value); setUploadSignedByCounterparty(''); }}
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        background: uploadOrigin === opt.value ? 'var(--bcore-hover-subtle)' : 'transparent',
                        border: uploadOrigin === opt.value ? '1px solid var(--bcore-accent)' : '1px solid var(--bcore-border, #333)',
                        borderRadius: '8px',
                        color: uploadOrigin === opt.value ? 'var(--bcore-accent)' : 'var(--bcore-text-primary)',
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: '13px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Gating Question 2: If B/CORE, is it signed? */}
              {uploadOrigin === 'bcore' && (
                <div style={{ marginBottom: '16px' }}>
                  <label className="form-label">Is the agreement signed by B/CORE and the Counterparty? <span className="required">*</span></label>
                  <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                    {[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setUploadSignedByCounterparty(opt.value)}
                        style={{
                          flex: 1,
                          padding: '12px 16px',
                          background: uploadSignedByCounterparty === opt.value ? 'var(--bcore-hover-subtle)' : 'transparent',
                          border: uploadSignedByCounterparty === opt.value ? '1px solid var(--bcore-accent)' : '1px solid var(--bcore-border, #333)',
                          borderRadius: '8px',
                          color: uploadSignedByCounterparty === opt.value ? 'var(--bcore-accent)' : 'var(--bcore-text-primary)',
                          fontFamily: 'JetBrains Mono, monospace',
                          fontSize: '13px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Block message if B/CORE originated but not signed */}
              {uploadOrigin === 'bcore' && uploadSignedByCounterparty === 'no' && (
                <div style={{
                  padding: '16px 20px',
                  background: 'rgba(231, 76, 60, 0.1)',
                  border: '1px solid rgba(231, 76, 60, 0.3)',
                  borderRadius: '8px',
                  marginBottom: '16px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '13px', color: '#e74c3c' }}>
                      Please acquire a signed agreement by all parties before proceeding.
                    </span>
                  </div>
                </div>
              )}

              {/* Show form fields only when allowed */}
              {showUploadFormFields && <>

              {/* Row 1: Type + Status */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label className="form-label">Type <span className="required">*</span></label>
                  <select className="form-select" value={uploadForm.type} onChange={(e) => handleFormChange('type', e.target.value)}>
                    <option value="">Select...</option>
                    <option value="NDA">NDA</option>
                    <option value="TA" disabled>TA — Coming Soon</option>
                  </select>
                  {uploadFormErrors.type && <div className="form-error">{uploadFormErrors.type}</div>}
                </div>
                <div>
                  <label className="form-label">Status <span className="required">*</span></label>
                  <select className="form-select" value={uploadForm.status} onChange={(e) => handleFormChange('status', e.target.value)}>
                    <option value="">Select...</option>
                    {UPLOAD_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {uploadFormErrors.status && <div className="form-error">{uploadFormErrors.status}</div>}
                </div>
              </div>

              {/* Row 2: Counterparty Name + Short Name */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label className="form-label">Counterparty Name <span className="required">*</span></label>
                  <input className="form-input" value={uploadForm.counterpartyName} onChange={(e) => handleFormChange('counterpartyName', e.target.value)} placeholder="e.g., Northrop Grumman" />
                  {uploadFormErrors.counterpartyName && <div className="form-error">{uploadFormErrors.counterpartyName}</div>}
                </div>
              </div>

              {/* Row 3: Opportunity + Owner */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label className="form-label">Opportunity Name</label>
                  <input className="form-input" value={uploadForm.opportunityName} onChange={(e) => handleFormChange('opportunityName', e.target.value)} placeholder="Optional" />
                </div>
                <div>
                  <label className="form-label">Opportunity Owner</label>
                  <input className="form-input" value={uploadForm.opportunityOwner} onChange={(e) => handleFormChange('opportunityOwner', e.target.value)} placeholder="your.email@bcore.com" />
                </div>
              </div>

              {/* Row 4: Terms + Duration */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label className="form-label">Terms <span className="required">*</span></label>
                  <select className="form-select" value={uploadForm.termsType} onChange={(e) => handleFormChange('termsType', e.target.value)}>
                    <option value="">Select...</option>
                    <option value="standard">Standard</option>
                    <option value="non-standard">Non-Standard</option>
                  </select>
                  {uploadFormErrors.termsType && <div className="form-error">{uploadFormErrors.termsType}</div>}
                </div>
                <div>
                  <label className="form-label">Term Duration <span className="required">*</span></label>
                  <select className="form-select" value={uploadForm.termDuration} onChange={(e) => handleFormChange('termDuration', e.target.value)}>
                    <option value="">Select...</option>
                    {TERM_DURATION_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  {uploadFormErrors.termDuration && <div className="form-error">{uploadFormErrors.termDuration}</div>}
                </div>
              </div>

              {/* Notes */}
              <div style={{ marginBottom: '16px' }}>
                <label className="form-label">Notes</label>
                <textarea
                  className="form-input"
                  value={uploadForm.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  placeholder="Optional notes..."
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </div>

              {/* File Drop Zone */}
              <div style={{ marginBottom: '24px' }}>
                <label className="form-label">Document (optional)</label>
                <div
                  className={`upload-dropzone${dragActive ? ' drag-active' : ''}${uploadFile ? ' has-file' : ''}`}
                  onDragEnter={handleDrag}
                  onDragOver={handleDrag}
                  onDragLeave={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => !uploadFile && fileInputRef.current?.click()}
                  style={{ cursor: uploadFile ? 'default' : 'pointer', marginTop: '6px' }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx"
                    style={{ display: 'none' }}
                    onChange={(e) => { handleFile(e.target.files[0]); e.target.value = ''; }}
                  />
                  {uploadFile ? (
                    <div className="upload-file-info">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ flexShrink: 0 }}>
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="upload-file-name">{uploadFile.name}</div>
                        <div className="upload-file-size">{formatFileSize(uploadFile.size)}</div>
                      </div>
                      <button
                        type="button"
                        className="upload-file-remove"
                        onClick={(e) => { e.stopPropagation(); setUploadFile(null); }}
                      >
                        &times;
                      </button>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: '8px', opacity: 0.4 }}>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      <div style={{ fontSize: '12px', color: 'var(--bcore-text-dim)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Drop PDF or DOCX here, or click to browse
                      </div>
                    </div>
                  )}
                </div>
                {uploadFormErrors.file && <div className="form-error">{uploadFormErrors.file}</div>}
              </div>

              </>}

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid var(--bcore-border)', paddingTop: '20px' }}>
                <button type="button" className="btn btn-outline" onClick={closeUploadModal}>Cancel</button>
                {showUploadFormFields && <button type="submit" className="btn btn-primary">Upload Agreement</button>}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgreementReview;
